import { useState } from "react";
import { Space, Button, Upload } from "antd";
import type { UploadProps } from "antd";
import { DownloadOutlined, UploadOutlined, CloseOutlined } from "@ant-design/icons";

import { getCustomRequest } from "@components/Import";

import useExport from "./helpers/useExport";
import useTemplateParser from "./helpers/useTemplateParser";
import type { SetState, TableRef, Investment, ImportError, CustomAmounts } from "./types";

const LABEL_UPLOAD_TEMPLATE = "Select Template";
const LABEL_DOWNLOAD_TEMPLATE = "CSV Template";

type Props = {
  table: TableRef;
  setErrors: SetState<ImportError[]>;
  sourceProjectId: string;
  targetProjectId: string;
  setCustomAmounts: SetState<CustomAmounts[]>;
  sourceInvestments: Investment[];
};

const Import = ({
  table,
  setErrors,
  sourceProjectId,
  targetProjectId,
  setCustomAmounts,
  sourceInvestments,
}: Props) => {
  const [ file, setFile ] = useState<File | undefined>();

  const getItems = () => table.current.getDataSource().filter(({ isIncluded }) => isIncluded);

  const onExportClick = useExport({
    getItems,
    sourceProjectId,
    targetProjectId,
    sourceInvestments,
  });

  const { parseRow, validateRow, buildRow } = useTemplateParser(setErrors, getItems, targetProjectId);

  const callbackUpload = (selectedFile: File) => setFile(selectedFile);
  const callbackSuccess = (value: CustomAmounts[]) => setCustomAmounts(value);

  const uploadRequest = getCustomRequest(parseRow, validateRow, buildRow, callbackSuccess, callbackUpload);

  const customRequest: UploadProps["customRequest"] = ({ file: uploadedFile, onSuccess }) => {
    uploadRequest({ file: uploadedFile, onSuccess });
  };

  const onReset = () => {
    setFile(undefined);
    setErrors([]);
    setCustomAmounts([]);
  };

  return (
    <Space>
      <Button
        icon={<DownloadOutlined />}
        onClick={onExportClick}
      >
        {LABEL_DOWNLOAD_TEMPLATE}
      </Button>

      {
        !file && (
          <Upload
            accept=".csv"
            multiple={false}
            customRequest={customRequest}
            showUploadList={false}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
            >
              {LABEL_UPLOAD_TEMPLATE}
            </Button>
          </Upload>
        )
      }

      {
        file && (
          <span>
            {file.name}
          </span>
        )
      }

      {
        file && (
          <Button
            icon={<CloseOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={onReset}
          />
        )
      }
    </Space>
  );
};

export default Import;
